import {Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from "@angular/core";
import {localizationKey, LocalizationStrings} from "../../../../../i18n/i18n-model";
import {AnalysisGenerationConfig, DoctorFindings} from "../../../analysis";
import {FormFieldConfig} from "../../../../../tools/form/form-field-config";
import {DownloadExternalFindingsService} from "../../download-external-findings.service";
import {generateFindings} from "./generate-findings/generate-findings";
import {SelectionGeneratedField} from "../widgets/selection-generator/selection-generated.field";
import {LanguageProvider} from "../../../../../i18n/language-provider";
import {joinSentences} from "../join-sentences";
import {areExternalFindingsEqual} from "./are-external-findings-equal";
import {areExternalFindingsMissingFields} from "./are-external-findings-missing-fields";
import {ExternalFindings} from "../../external-findings";
import {PreanalysisEcgData} from "../../../finish-analysis.service";
import {generateAvConductionsBlobText} from "./generate-av-conductions-blob-text";
import {ReleaseToggleState} from "../../../../../tools/release-toggles/release-toggle-state";
import {MeasurementType} from "../../../../measurement-models";
import {ExternalAnalysisType} from "../../../../hospitals/edit-hospital/hospital.details";
import {AvConduction} from "../../ecg-preanalysis-data/ecg-preanalysis-data.component";
import {cardiologsFindingsDescriptions, casFindingsDescriptions, FindingsDescriptions} from "./findings-descriptions";


export interface ValueFindings {
  readonly prevailingRhythm: ValueGeneratorData;
  readonly averageHeartRate: ValueGeneratorData;
  readonly minimumHeartRate: ValueGeneratorData;
  readonly maximumHeartRate: ValueGeneratorData;
  readonly pausesOverThreshold: ValueGeneratorData;
  readonly ventricularBeats: ValueGeneratorData;
  readonly ventricularRuns: ValueGeneratorData;
  readonly ventricularTachycardias: ValueGeneratorData;
  readonly slowVentricularRhythms: ValueGeneratorData;
  readonly commentVentricular: ValueGeneratorData;
  readonly atrialBeats: ValueGeneratorData;
  readonly supraventricularRuns: ValueGeneratorData;
  readonly atrialTachycardias: ValueGeneratorData;
  readonly commentAtrial: ValueGeneratorData;
  readonly symptomDiaryComment: ValueGeneratorData;
}

export interface FullPreanalysisEcgData {
  readonly beatMeasurements: BeatMeasurement[];
  readonly detectedAvConductions: AvConduction[];
}

export interface StripTime {
  readonly time: string;
  readonly day: number;
}

export interface BeatMeasurement {
  readonly stripTime: StripTime;
  readonly pq?: number;
  readonly qrs: number;
  readonly qt: number;
  readonly heartrate?: number;
}

export interface SelectionFindings {
  avConduction: SelectionGeneratedField;
  deltaWave: SelectionGeneratedField;
  qrsWidth: SelectionGeneratedField;
  qtTime: SelectionGeneratedField;
  stSegment: SelectionGeneratedField;
}

export interface ValueGeneratorData {
  value: string | undefined;
  text: string | undefined;
}

function createBlankValueGeneratorData(): ValueGeneratorData {
  return {
    value: "",
    text: ""
  };
}

@Component({
    selector: "app-findings-generator",
    templateUrl: "./findings-generator.component.html",
    standalone: false
})
export class FindingsGeneratorComponent implements OnInit {

  @Input()
  analysisGenerationConfig?: AnalysisGenerationConfig;

  @Input()
  config?: FormFieldConfig<DoctorFindings>;

  @Input()
  measurementId?: number;

  @Output()
  readonly onGenerate = new EventEmitter<string>();

  @Input()
  isInitiallyManualMode = false;

  @Input()
  preanalysisEcgData: PreanalysisEcgData | undefined;

  @Input()
  fullPreanalysisEcgData: FullPreanalysisEcgData | undefined;

  @Input()
  measurementType!: MeasurementType;

  @Input()
  externalAnalysisType!: ExternalAnalysisType;

  isManualMode = false;

  areValuesOutdated = false;

  isWaitingForRegenerateConfirmation = false;

  isWaitingForOutdatedConfirm = false;

  areExternalFindingsMissingFields = false;

  valueFindings: ValueFindings = {
    prevailingRhythm: createBlankValueGeneratorData(),
    averageHeartRate: createBlankValueGeneratorData(),
    minimumHeartRate: createBlankValueGeneratorData(),
    maximumHeartRate: createBlankValueGeneratorData(),
    pausesOverThreshold: createBlankValueGeneratorData(),
    ventricularBeats: createBlankValueGeneratorData(),
    ventricularRuns: createBlankValueGeneratorData(),
    ventricularTachycardias: createBlankValueGeneratorData(),
    slowVentricularRhythms: createBlankValueGeneratorData(),
    atrialBeats: createBlankValueGeneratorData(),
    supraventricularRuns: createBlankValueGeneratorData(),
    atrialTachycardias: createBlankValueGeneratorData(),
    commentVentricular: createBlankValueGeneratorData(),
    commentAtrial: createBlankValueGeneratorData(),
    symptomDiaryComment: createBlankValueGeneratorData(),
  };

  selectionFindings?: SelectionFindings;

  descriptions: FindingsDescriptions = cardiologsFindingsDescriptions;

  @ViewChild("templateConfirmPatientStatusCommentsRegenerate")
  templateConfirmPatientStatusCommentsRegenerate!: TemplateRef<any>;

  preview = "";

  private previousExternalFindings: ExternalFindings | undefined;

  constructor(
    private readonly downloadExternalFindingsService: DownloadExternalFindingsService,
    private readonly languageProvider: LanguageProvider
  ) {
  }

  ngOnInit(): void {
    this.isManualMode = this.isInitiallyManualMode;
    this.descriptions = this.isCas() ? casFindingsDescriptions : cardiologsFindingsDescriptions;
    this.selectionFindings = this.createFindingsGeneratorOptions();

    if (
      this.preanalysisEcgData !== undefined
      && !this.isInitiallyManualMode
    ) {
      setTimeout(() => this.getExternalFindings());
    }
  }

  findingGenerated(findingType: keyof ValueFindings, generatedText: string) {
    this.valueFindings[findingType].text = generatedText;
    this.generatePreview();
  }

  private generatePreview(): void {

    const preview: (string | undefined)[] = [
      this.valueFindings.prevailingRhythm.text,
      this.valueFindings.averageHeartRate.text,
      this.valueFindings.minimumHeartRate.text,
      this.valueFindings.maximumHeartRate.text,
      this.valueFindings.pausesOverThreshold.text,
      this.valueFindings.ventricularBeats.text,
      this.valueFindings.ventricularTachycardias.text,
      this.valueFindings.ventricularRuns.text,
      this.valueFindings.slowVentricularRhythms.text,
      this.valueFindings.commentVentricular.text,
      this.valueFindings.atrialBeats.text,
      this.valueFindings.atrialTachycardias.text,
      this.valueFindings.supraventricularRuns.text,
      this.valueFindings.commentAtrial.text,
      this.selectionFindings!.avConduction.text,
      this.selectionFindings!.qrsWidth.text,
      this.selectionFindings!.deltaWave.text,
      this.selectionFindings!.qtTime.text,
      this.selectionFindings!.stSegment.text,
      this.valueFindings.symptomDiaryComment.text
    ];

    const content = joinSentences(preview);

    this.preview = content;
    this.onGenerate.emit(content);
  }

  regenerate() {
    this.isManualMode = false;
    this.generatePreview();
  }

  localizationKey = localizationKey;

  getExternalFindings() {
    this.downloadExternalFindingsService
      .getExternalFindings(this.measurementId!)
      .subscribe({
        next: externalFindings => {
          this.valueFindings = generateFindings({
              externalFindings,
              externalAnalysisType: this.externalAnalysisType,
              analysisGenerationConfig: this.analysisGenerationConfig!,
              previousFindings: this.valueFindings,
              locale: this.languageProvider.getLanguage()
            }
          );
          this.previousExternalFindings = externalFindings;
          this.areValuesOutdated = false;
          this.generatePreview();
          this.areExternalFindingsMissingFields = areExternalFindingsMissingFields(externalFindings, this.externalAnalysisType);
        }
      });
  }

  private createFindingsGeneratorOptions(): SelectionFindings {
    return {
      avConduction: new SelectionGeneratedField(
        [
          {
            optionLabel: "generator_findings_option_normal",
            generatedText: this.analysisGenerationConfig!.findings_avConduction_normal
          }
        ]),
      deltaWave: new SelectionGeneratedField([
        {
          optionLabel: "generator_findings_option_normal",
          generatedText: this.analysisGenerationConfig!.findings_deltaWave_normal
        }
      ]),
      qtTime: new SelectionGeneratedField([
        {
          optionLabel: "generator_findings_option_normal",
          generatedText: this.analysisGenerationConfig!.findings_qtTime_normal
        }
      ]),
      qrsWidth: new SelectionGeneratedField([
        {
          optionLabel: "generator_findings_option_normal",
          generatedText: this.analysisGenerationConfig!.findings_qrsWidth_normal
        }
      ]),
      stSegment: new SelectionGeneratedField([
        {
          optionLabel: "generator_findings_option_normal",
          generatedText: this.analysisGenerationConfig!.findings_stSegment_normal
        }
      ])
    };
  };

  selectionFindingGenerated(field: keyof SelectionFindings, newValue: string) {
    this.selectionFindings![field].text = newValue;
    this.generatePreview();
  }

  switchToManualMode() {
    this.isWaitingForOutdatedConfirm = true;
    this.downloadExternalFindingsService
      .getExternalFindings(this.measurementId!)
      .subscribe(externalFindings => {

          const valuesAreOutdated =
            this.previousExternalFindings !== undefined
            && !areExternalFindingsEqual(externalFindings, this.previousExternalFindings);

          this.isManualMode = !valuesAreOutdated;
          this.areValuesOutdated = valuesAreOutdated;
        },
        () => {
        },
        () => {
          this.isWaitingForOutdatedConfirm = false;
        });
  }

  generatePreanalysisEcgBlobValue(
    parameters: {
      interval: number | undefined;
      heartRate: number | undefined;
    } | undefined
  ) {
    if (this.preanalysisEcgData === undefined) {
      return [];
    }

    return [parameters
      ? `${parameters.interval} ms (${parameters.heartRate}/min)`
      : "?"];
  }

  generatePqBadges(): string[] {

    if (this.fullPreanalysisEcgData !== undefined) {
      const avConductionText = generateAvConductionsBlobText(
        this.fullPreanalysisEcgData.detectedAvConductions,
        this.analysisGenerationConfig!.findings_avConduction_otherAvb!
      );
      return avConductionText === "" ? [] : [avConductionText];
    }

    if (this.preanalysisEcgData === undefined) {
      return [];
    }

    const avConductionText = generateAvConductionsBlobText(this.preanalysisEcgData.avConductions);

    return [
      ...generatePqIntervalBadges(this.preanalysisEcgData),
      ...(avConductionText === "" ? [] : [avConductionText])
    ];
  }

  ventricularTachycardiasLabel(): keyof LocalizationStrings {
    return this.externalAnalysisType === "CAS"
      ? "generator_label_findings_ventricularTachycardiasCas"
      : "generator_label_findings_ventricularTachycardias";
  }

  atrialTachycardiasLabel(): keyof LocalizationStrings {
    return this.externalAnalysisType === "CAS"
      ? "generator_label_findings_atrialTachycardiasCas"
      : "generator_label_findings_atrialTachycardias";
  }

  pausesLabel(): keyof LocalizationStrings {
    return this.externalAnalysisType === "CAS"
      ? "generator_label_findings_pausesOver2s"
      : "generator_label_findings_pausesOver2_5s";
  }

  shouldShowSymptomComments() {
    return this.measurementType !== "SYMPTOM_HOLTER"
      || !ReleaseToggleState.getInstance().isReleased("HIDE_ITEMS_IN_REPORT_TOOL_BASED_ON_MEASUREMENT_TYPE_535");
  }

  isCas(): boolean {
    return this.externalAnalysisType === "CAS";
  }
}

function generatePqIntervalBadges(preanalysisEcgData: PreanalysisEcgData) {
  if (preanalysisEcgData!.pq === undefined) {
    return ["?"];
  }

  return [
    preanalysisEcgData.pq,
    preanalysisEcgData.pq2,
    preanalysisEcgData.pq3
  ]
    .filter(data => data !== undefined)
    .map(data => `${data!.interval} ms @ ${data!.heartRate}/min`);
}
